import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, ArrowRight } from 'lucide-react';
import { trackLead } from '../utils/metaPixel';
interface LeadFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'full' | 'notify';
  resourceTitle?: string;
}
export function LeadFormModal({
  isOpen,
  onClose,
  mode = 'full',
  resourceTitle
}: LeadFormModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    scoreRange: '',
    mainGoal: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.id]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg('');
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const payload = {
        ...formData,
        source: 'blinkblis_landing_v1',
        timestamp: new Date().toISOString(),
        utm_source: urlParams.get('utm_source') || '',
        utm_medium: urlParams.get('utm_medium') || '',
        utm_campaign: urlParams.get('utm_campaign') || '',
        click_id: urlParams.get('click_id') || ''
      };
      console.log('Form Payload:', payload);
      const FORM_ENDPOINT = 'https://hooks.zapier.com/hooks/catch/PLACEHOLDER/PLACEHOLDER/';
      // We use a try/catch block for the fetch to handle network errors gracefully,
      // but we'll simulate success if the placeholder URL is still present
      // to ensure the UI still works during development/testing.
      if (FORM_ENDPOINT.includes('PLACEHOLDER')) {
        await new Promise(resolve => setTimeout(resolve, 1500));
      } else {
        const response = await fetch(FORM_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
      }
      // Fire Meta Pixel Lead event
      trackLead(mode === 'notify' ? 'notify_form' : 'playbook_form', {
        score_range: formData.scoreRange,
        main_goal: formData.mainGoal
      });
      setIsSubmitting(false);
      setIsSuccess(true);
      if (mode === 'notify') {
        setTimeout(() => {
          setIsSuccess(false);
          setFormData({
            firstName: '',
            email: '',
            scoreRange: '',
            mainGoal: ''
          });
          onClose();
        }, 3000);
      }
    } catch (err) {
      setIsSubmitting(false);
      setErrorMsg('System error during submission. Please try again.');
    }
  };
  const handleSeeComparison = () => {
    onClose();
    setTimeout(() => {
      document.getElementById('comparison')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      setIsSuccess(false);
      setFormData({
        firstName: '',
        email: '',
        scoreRange: '',
        mainGoal: ''
      });
    }, 200);
  };
  const getRecommendation = () => {
    switch (formData.scoreRange) {
      case 'Below 580':
        return 'Based on your score, we recommend starting with Kovo ($10/mo, no credit check, all 4 bureaus).';
      case '580-669':
        return 'Based on your score, Kovo or Self are strong matches.';
      case '670-739':
        return 'Based on your score, CreditStrong offers higher limits worth exploring.';
      case '740+':
        return 'Your credit is strong — explore premium credit cards to maximize rewards.';
      case "I don't know":
      default:
        return 'A safe starting point is Chime Credit Builder — free, no credit check, low risk.';
    }
  };
  return <AnimatePresence data-id="element-306">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-307">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-navy/60 backdrop-blur-sm" data-id="element-308" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto" data-id="element-309">
            <button onClick={onClose} className="absolute top-4 right-4 text-slate hover:text-charcoal transition-colors z-10" aria-label="Close modal" data-id="element-310">
              <X className="w-6 h-6" data-id="element-311" />
            </button>

            {isSuccess ? <div className="p-8 text-center flex flex-col items-center justify-center h-full min-h-[400px]" data-id="element-312">
                <div className="w-16 h-16 bg-emerald/10 rounded-full flex items-center justify-center mb-6" data-id="element-313">
                  <ShieldCheck className="w-8 h-8 text-emerald" data-id="element-314" />
                </div>

                {mode === 'notify' ? <>
                    <h3 className="text-2xl font-bold text-charcoal mb-2" data-id="element-315">
                      Notification Registered
                    </h3>
                    <p className="text-slate leading-[1.6]" data-id="element-316">
                      You will receive an alert when the resource is published.
                    </p>
                  </> : <>
                    <h3 className="text-2xl font-bold text-charcoal mb-2" data-id="element-317">
                      Playbook Dispatched
                    </h3>
                    <p className="text-slate leading-[1.6] mb-8" data-id="element-318">
                      The documentation has been sent to your email.
                    </p>

                    <div className="bg-offwhite rounded-xl p-5 mb-8 w-full text-left border border-gray-100" data-id="element-319">
                      <p className="text-charcoal font-medium leading-[1.6]" data-id="element-320">
                        {getRecommendation()}
                      </p>
                    </div>

                    <button onClick={handleSeeComparison} className="text-emerald font-bold hover:text-emerald-500 transition-colors flex items-center" data-id="element-321">
                      Review monitoring infrastructure{' '}
                      <ArrowRight className="ml-1 w-4 h-4" data-id="element-322" />
                    </button>
                  </>}
              </div> : <div className="p-6 sm:p-8" data-id="element-323">
                <div className="mb-6" data-id="element-324">
                  <h3 className="text-2xl font-bold text-charcoal mb-2" data-id="element-325">
                    {mode === 'notify' ? `Get notified when ${resourceTitle} launches` : 'Access The Playbook'}
                  </h3>
                  {mode === 'full' && <p className="text-slate font-medium text-sm leading-[1.6]" data-id="element-326">
                      Review the reporting frameworks and dispute workflows used
                      to analyze bureau inconsistencies.
                    </p>}
                </div>

                {errorMsg && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg font-medium" data-id="element-327">
                    {errorMsg}
                  </div>}

                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-328">
                  {mode === 'full' && <div data-id="element-329">
                      <label htmlFor="firstName" className="block text-sm font-bold text-charcoal mb-1" data-id="element-330">
                        First Name
                      </label>
                      <input type="text" id="firstName" required value={formData.firstName} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-emerald focus:ring-2 focus:ring-emerald/20 outline-none transition-all" placeholder="Enter your first name" data-id="element-331" />
                    </div>}

                  <div data-id="element-332">
                    <label htmlFor="email" className="block text-sm font-bold text-charcoal mb-1" data-id="element-333">
                      Email Address
                    </label>
                    <input type="email" id="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-emerald focus:ring-2 focus:ring-emerald/20 outline-none transition-all" placeholder="Where should we send it?" data-id="element-334" />
                  </div>

                  {mode === 'full' && <>
                      <div data-id="element-335">
                        <label htmlFor="scoreRange" className="block text-sm font-bold text-charcoal mb-1" data-id="element-336">
                          Current Credit Score Range
                        </label>
                        <select id="scoreRange" required value={formData.scoreRange} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-emerald focus:ring-2 focus:ring-emerald/20 outline-none transition-all bg-white" data-id="element-337">
                          <option value="" data-id="element-338">Select a range</option>
                          <option value="Below 580" data-id="element-339">Below 580</option>
                          <option value="580-669" data-id="element-340">580-669</option>
                          <option value="670-739" data-id="element-341">670-739</option>
                          <option value="740+" data-id="element-342">740+</option>
                          <option value="I don't know" data-id="element-343">I don't know</option>
                        </select>
                      </div>

                      <div data-id="element-344">
                        <label htmlFor="mainGoal" className="block text-sm font-bold text-charcoal mb-1" data-id="element-345">
                          Main Goal
                        </label>
                        <select id="mainGoal" required value={formData.mainGoal} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-emerald focus:ring-2 focus:ring-emerald/20 outline-none transition-all bg-white" data-id="element-346">
                          <option value="" data-id="element-347">Select your goal</option>
                          <option value="Buy a home" data-id="element-348">Buy a home</option>
                          <option value="Buy a car" data-id="element-349">Buy a car</option>
                          <option value="Get approved for credit cards" data-id="element-350">
                            Get approved for credit cards
                          </option>
                          <option value="Improve general credit health" data-id="element-351">
                            Improve general credit health
                          </option>
                        </select>
                      </div>
                    </>}

                  <button type="submit" disabled={isSubmitting} className="w-full bg-emerald hover:bg-emerald-500 text-white font-bold py-4 px-6 rounded-lg shadow-cta hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed mt-6" data-id="element-352">
                    {isSubmitting ? <span className="flex items-center" data-id="element-353">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-354">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-355"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-356"></path>
                        </svg>
                        Processing Request...
                      </span> : <span className="flex items-center" data-id="element-357">
                        {mode === 'notify' ? 'Register Notification' : 'Access Documentation'}{' '}
                        <ArrowRight className="ml-2 w-5 h-5" data-id="element-358" />
                      </span>}
                  </button>

                  <p className="text-xs font-medium text-center text-slate mt-4 flex items-center justify-center" data-id="element-359">
                    <ShieldCheck className="w-4 h-4 mr-1 text-emerald" data-id="element-360" />
                    Data submitted is secure and never shared.
                  </p>
                </form>
              </div>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}